import React, { useEffect, useRef, useState, useCallback } from 'react';

const DirectVideoStream = ({ 
  sessionId, 
  // onInteraction is no longer needed
  className = "",
  style = {},
  isConnected,
  sendClick,
  sendScroll,
  sendKeyboard,
  attachVideo
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Attach video element to the hook when it's ready
  const videoElRef = useCallback(node => {
      if (node && attachVideo) {
          videoRef.current = node;
          attachVideo(node);
      }
  }, [attachVideo]);

  // Handle video clicks
  const handleVideoClick = (event) => {
    if (!isConnected || !videoRef.current) return;

    const rect = videoRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    const button = event.button === 2 ? 'right' : 'left';
    sendClick(x, y, button, {
      videoWidth: rect.width,
      videoHeight: rect.height
    });
  };

  // Handle video scroll
  const handleVideoScroll = (event) => {
    if (!isConnected || !videoRef.current) return;
    event.preventDefault();
    
    const rect = videoRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    sendScroll(x, y, event.deltaX, event.deltaY, {
        videoWidth: rect.width,
        videoHeight: rect.height
    });
  };

  // Handle keyboard input
  const handleKeyDown = (event) => {
    if (!isConnected) return;
    
    // Prevent default for functional keys to avoid browser shortcuts
    if (['Tab', 'Backspace', 'Enter', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(event.key)) {
      event.preventDefault();
    }

    sendKeyboard(event.key);
  };

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Update fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Auto-focus for keyboard input
  useEffect(() => {
    if (containerRef.current && isConnected) {
      containerRef.current.focus();
    }
  }, [isConnected]);

  // --- NEW: Manually attach wheel event listener to control passive option ---
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // The `{ passive: false }` option is crucial. It tells the browser that we
    // intend to call event.preventDefault(), and the browser must wait for our
    // handler to finish before attempting to scroll the page.
    videoElement.addEventListener('wheel', handleVideoScroll, { passive: false });

    // Cleanup: remove the event listener when the component unmounts.
    return () => {
      videoElement.removeEventListener('wheel', handleVideoScroll);
    };
  }, [handleVideoScroll]); // Re-attach if the handler function changes

  return (
    <div 
      ref={containerRef}
      className={`direct-video-stream ${className} ${isFullscreen ? 'fullscreen' : ''}`}
      style={{
        position: 'relative',
        outline: 'none',
        backgroundColor: '#000',
        ...style
      }}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <video
        ref={videoElRef}
        className="direct-video-element"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: 'crosshair',
        }}
        autoPlay
        playsInline
        onClick={handleVideoClick}
        onContextMenu={(e) => {
            e.preventDefault();
            handleVideoClick(e);
        }}
        // onWheel is now handled by the useEffect hook above
      />

      {/* Removed connection overlay to allow interaction immediately */}

      {/* Controls */}
      <div className="video-controls" style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        display: 'flex',
        gap: '8px',
      }}>
        <button
          onClick={toggleFullscreen}
          style={{
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid #fff',
            color: 'white',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%'
          }}
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        >
          {isFullscreen ? '↘' : '↗'}
        </button>
      </div>
    </div>
  );
};

export default DirectVideoStream; 